import type { WizardFeatureContext } from '@/components/_championship/forms/types/wizard'
import { getWizardFlowOptions } from '@/components/_championship/forms/wizard-sync/flow-options'
import type { WizardStepKey } from '@/components/_championship/forms/wizard-sync/step-order'
import {
  CarFront,
  FileIcon,
  Layers,
  ListIcon,
  Mail,
  Navigation,
  PlayCircle,
  Users,
  Wrench,
} from 'lucide-react'
import type { ReactNode } from 'react'

type SidebarItem = {
  title: string
  urlSnippet: WizardStepKey
  icon: ReactNode
}

type SidebarItemState = {
  optionalDisabled: boolean
}

const sidebarItems: SidebarItem[] = [
  {
    title: 'Configuration',
    urlSnippet: 'configuration',
    icon: <Wrench />,
  },
  {
    title: 'Cars & Grid Size',
    urlSnippet: 'car-classes',
    icon: <CarFront />,
  },
  {
    title: 'Teams',
    urlSnippet: 'teams',
    icon: <Users />,
  },
  {
    title: 'Rules',
    urlSnippet: 'rules',
    icon: <FileIcon />,
  },
  {
    title: 'Tiers / Splits',
    urlSnippet: 'tiers-and-splits',
    icon: <Layers />,
  },
  {
    title: 'Application Form',
    urlSnippet: 'application-form',
    icon: <ListIcon />,
  },
  {
    title: 'Events',
    urlSnippet: 'events',
    icon: <Navigation />,
  },
  {
    title: 'Pre-Event Emails',
    urlSnippet: 'server-info',
    icon: <Mail />,
  },
  {
    title: 'Review',
    urlSnippet: 'review',
    icon: <PlayCircle />,
  },
]

const isSidebarItemEnabledByContext = (
  item: SidebarItem,
  context: WizardFeatureContext | null,
): boolean => {
  if (!context) return true

  if (item.urlSnippet === 'teams') return context.teamsEnabled !== false
  if (item.urlSnippet === 'rules') return context.rulesEnabled !== false
  if (item.urlSnippet === 'tiers-and-splits')
    return context.tiersAndSplitsEnabled !== false
  if (item.urlSnippet === 'application-form')
    return context.applicationFormEnabled !== false
  if (item.urlSnippet === 'server-info')
    return context.serverInfoEnabled !== false

  return true
}

const getSidebarItemStateForChampionship = (
  championshipId: string,
): Partial<Record<WizardStepKey, SidebarItemState>> => {
  const context = getWizardFlowOptions(championshipId)
  if (!context) return {}

  return sidebarItems.reduce<Partial<Record<WizardStepKey, SidebarItemState>>>(
    (acc, item) => {
      acc[item.urlSnippet] = {
        optionalDisabled: !isSidebarItemEnabledByContext(item, context),
      }
      return acc
    },
    {},
  )
}

/**
 * Check if a given urlSnippet can be skipped to in the navigation
 */
const canSkipToSection = (
  urlSnippet: WizardStepKey,
  storedStep: string | null,
): boolean => {
  if (!storedStep) return urlSnippet === 'configuration'

  const currentIndex = sidebarItems.findIndex(
    (item) => item.urlSnippet === storedStep,
  )
  const targetIndex = sidebarItems.findIndex(
    (item) => item.urlSnippet === urlSnippet,
  )

  return targetIndex <= currentIndex
}

/**
 * Set the step of the current championship form, always ensuring we can skip to the furthest step if the user has already gone back
 */
const setStoredStep = (
  nextUrlSnippet: WizardStepKey,
  championshipId: string,
) => {
  const currentStoredStep = window.localStorage.getItem(
    `championship_${championshipId}_form`,
  )

  const currentIndex = sidebarItems.findIndex(
    (item) => item.urlSnippet === currentStoredStep,
  )
  const targetIndex = sidebarItems.findIndex(
    (item) => item.urlSnippet === nextUrlSnippet,
  )

  const furthestStepIndex =
    targetIndex <= currentIndex ? currentIndex : targetIndex

  const furthestStep = sidebarItems[furthestStepIndex]?.urlSnippet

  if (!furthestStep) return

  window.localStorage.setItem(
    `championship_${championshipId}_form`,
    furthestStep,
  )
}

export {
  canSkipToSection,
  getSidebarItemStateForChampionship,
  setStoredStep,
  sidebarItems,
}
